import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
  transferArrayItem
} from '@angular/cdk/drag-drop';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';

import { ApiService } from '../../core/services/api.service';
import { ScrumApiService } from './scrum.service';
import { IssueDrawerService } from '../../shared/issue-drawer/issue-drawer.service';
import { deadlineBadgeClass, deadlineDisplayLabel } from '../shared/deadline.utils';
import {
  BacklogIssueDto,
  SprintBoardColumnDto,
  SprintDto,
  SprintRetrospectiveDto,
  SPRINT_STATUS_LABELS
} from './scrum.models';

interface ProjectRow {
  id: string;
  name: string;
  code?: string;
}

interface BoardColumnView {
  key: string;
  label: string;
  statuses: number[];
  issues: BacklogIssueDto[];
}

@Component({
  selector: 'app-scrum',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    DragDropModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatChipsModule
  ],
  templateUrl: './scrum.component.html',
  styleUrl: './scrum.component.scss'
})
export class ScrumComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly scrumApi = inject(ScrumApiService);
  private readonly issueDrawer = inject(IssueDrawerService);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly projects = signal<ProjectRow[]>([]);
  readonly sprints = signal<SprintDto[]>([]);
  readonly columns = signal<BoardColumnView[]>([]);
  readonly backlog = signal<BacklogIssueDto[]>([]);
  readonly selectedProjectId = signal<string | null>(null);
  readonly selectedSprintId = signal<string | null>(null);
  readonly selectedSprint = signal<SprintDto | null>(null);
  readonly overdueCount = signal(0);
  readonly completionPercent = signal(0);
  readonly sprintDelayDays = signal(0);
  readonly sprintHealthLabel = signal('Healthy');

  newSprintName = '';
  newSprintGoal = '';
  newSprintStart = '';
  newSprintEnd = '';
  newSprintCapacityPoints: number | null = null;
  newSprintCapacityHours: number | null = null;
  editCapacityPoints: number | null = null;
  editCapacityHours: number | null = null;
  retroWhatWentWell = '';
  retroWhatToImprove = '';
  retroActionItems = '';
  readonly retrospective = signal<SprintRetrospectiveDto | null>(null);
  readonly savingRetro = signal(false);
  showCompleteDialog = false;
  completeDestination: 'backlog' | 'sprint' = 'backlog';
  carryToSprintId = '';

  readonly statusLabels = SPRINT_STATUS_LABELS;

  private readonly defaultColumns: BoardColumnView[] = [
    { key: 'todo', label: 'To Do', statuses: [0, 5], issues: [] },
    { key: 'inProgress', label: 'In Progress', statuses: [1], issues: [] },
    { key: 'blocked', label: 'Blocked', statuses: [6], issues: [] },
    { key: 'done', label: 'Done', statuses: [3, 4], issues: [] }
  ];

  ngOnInit(): void {
    const today = new Date();
    const end = new Date(today);
    end.setDate(end.getDate() + 14);
    this.newSprintStart = today.toISOString().slice(0, 10);
    this.newSprintEnd = end.toISOString().slice(0, 10);
    this.columns.set(this.emptyColumns());
    this.loadProjects();
  }

  private emptyColumns(): BoardColumnView[] {
    return this.defaultColumns.map(c => ({ ...c, issues: [] }));
  }

  loadProjects(): void {
    this.api.getPaged<ProjectRow>('v1/Projects', { page: 1, pageSize: 100 }).subscribe({
      next: (page) => {
        this.projects.set(page.items ?? []);
        if (page.items?.length && !this.selectedProjectId()) {
          this.selectProject(page.items[0].id);
        }
      },
      error: (err) => this.error.set(err?.message || 'Failed to load projects.')
    });
  }

  selectProject(projectId: string): void {
    this.selectedProjectId.set(projectId);
    this.selectedSprintId.set(null);
    this.selectedSprint.set(null);
    this.columns.set(this.emptyColumns());
    this.backlog.set([]);
    this.loadSprints(projectId);
    this.loadBacklog(projectId);
  }

  loadSprints(projectId: string): void {
    this.loading.set(true);
    this.scrumApi.listSprints({ page: 1, pageSize: 50, projectId }).subscribe({
      next: (page) => {
        const items = page.items ?? [];
        this.sprints.set(items);
        this.loading.set(false);
        const preferred =
          items.find(s => s.status === 1)
          ?? items.find(s => s.status === 0)
          ?? items[0];
        if (preferred) {
          this.selectSprint(preferred.id);
        } else {
          this.selectedSprintId.set(null);
          this.selectedSprint.set(null);
          this.columns.set(this.emptyColumns());
        }
      },
      error: (err) => {
        this.loading.set(false);
        this.error.set(err?.message || 'Failed to load sprints.');
      }
    });
  }

  loadBacklog(projectId: string): void {
    this.scrumApi.getBacklog(projectId, { page: 1, pageSize: 100 }).subscribe({
      next: (page) => this.backlog.set(page.items ?? []),
      error: () => this.backlog.set([])
    });
  }

  selectSprint(sprintId: string): void {
    this.selectedSprintId.set(sprintId);
    this.loadBoard(sprintId);
    this.loadRetrospective(sprintId);
  }

  loadBoard(sprintId: string): void {
    this.loading.set(true);
    this.error.set(null);
    this.scrumApi.getBoard(sprintId).subscribe({
      next: (data) => {
        this.selectedSprint.set(data.sprint);
        this.editCapacityPoints = data.sprint.capacityPoints ?? null;
        this.editCapacityHours = data.sprint.capacityHours ?? null;
        const mapped = (data.columns ?? []).map((c: SprintBoardColumnDto) => ({
          key: c.key,
          label: c.label,
          statuses: c.statuses ?? [],
          issues: [...(c.issues ?? [])]
        }));
        this.columns.set(mapped.length ? mapped : this.emptyColumns());
        this.backlog.set([...(data.backlog ?? [])]);
        this.overdueCount.set(data.overdueCount ?? 0);
        this.completionPercent.set(data.completionPercent ?? 0);
        this.sprintDelayDays.set(data.sprintDelayDays ?? 0);
        this.sprintHealthLabel.set(data.sprintHealthLabel || data.sprintHealth || 'Healthy');
        this.loading.set(false);
      },
      error: (err) => {
        this.loading.set(false);
        this.error.set(err?.message || 'Failed to load scrum board.');
      }
    });
  }

  createSprint(): void {
    const projectId = this.selectedProjectId();
    if (!projectId || !this.newSprintName.trim()) return;

    this.scrumApi.createSprint({
      projectId,
      name: this.newSprintName.trim(),
      goal: this.newSprintGoal || undefined,
      startDate: this.newSprintStart,
      endDate: this.newSprintEnd,
      capacityPoints: this.newSprintCapacityPoints ?? undefined,
      capacityHours: this.newSprintCapacityHours ?? undefined
    }).subscribe({
      next: (sprint) => {
        this.newSprintName = '';
        this.newSprintGoal = '';
        this.newSprintCapacityPoints = null;
        this.newSprintCapacityHours = null;
        this.loadSprints(projectId);
        if (sprint?.id) this.selectSprint(sprint.id);
      },
      error: (err) => this.error.set(err?.message || 'Failed to create sprint.')
    });
  }

  addToSprint(issue: BacklogIssueDto): void {
    const sprintId = this.selectedSprintId();
    if (!sprintId) return;
    this.scrumApi.addIssues(sprintId, [issue.id]).subscribe({
      next: () => this.loadBoard(sprintId),
      error: (err) => this.error.set(err?.message || 'Failed to add issue to sprint.')
    });
  }

  removeFromSprint(issue: BacklogIssueDto): void {
    const sprintId = this.selectedSprintId();
    if (!sprintId) return;
    this.scrumApi.removeIssue(sprintId, issue.id).subscribe({
      next: () => this.loadBoard(sprintId),
      error: (err) => this.error.set(err?.message || 'Failed to remove issue.')
    });
  }

  startSprint(): void {
    const sprintId = this.selectedSprintId();
    const projectId = this.selectedProjectId();
    if (!sprintId || !projectId) return;
    this.scrumApi.startSprint(sprintId).subscribe({
      next: () => {
        this.loadSprints(projectId);
        this.loadBoard(sprintId);
      },
      error: (err) => this.error.set(err?.message || 'Failed to start sprint.')
    });
  }

  completeSprint(): void {
    const sprintId = this.selectedSprintId();
    if (!sprintId) return;
    this.showCompleteDialog = true;
    this.completeDestination = 'backlog';
    const next = this.carryForwardCandidates()[0];
    this.carryToSprintId = next?.id ?? '';
  }

  cancelCompleteDialog(): void {
    this.showCompleteDialog = false;
  }

  carryForwardCandidates(): SprintDto[] {
    const currentId = this.selectedSprintId();
    return this.sprints().filter(s => s.id !== currentId && s.status === 0);
  }

  confirmCompleteSprint(): void {
    const sprintId = this.selectedSprintId();
    const projectId = this.selectedProjectId();
    if (!sprintId || !projectId) return;

    const toSprint = this.completeDestination === 'sprint' ? this.carryToSprintId : null;
    if (this.completeDestination === 'sprint' && !toSprint) {
      this.error.set('Select a planned sprint to carry incomplete work forward.');
      return;
    }

    this.scrumApi.completeSprint(
      sprintId,
      this.completeDestination === 'backlog',
      toSprint
    ).subscribe({
      next: () => {
        this.showCompleteDialog = false;
        this.loadSprints(projectId);
        this.loadBoard(sprintId);
        this.loadRetrospective(sprintId);
      },
      error: (err) => this.error.set(err?.message || 'Failed to complete sprint.')
    });
  }

  cancelSprint(): void {
    const sprintId = this.selectedSprintId();
    const projectId = this.selectedProjectId();
    if (!sprintId || !projectId) return;
    if (!confirm('Cancel this sprint? All issues will return to the backlog.')) return;
    this.scrumApi.cancelSprint(sprintId).subscribe({
      next: () => {
        this.loadSprints(projectId);
        this.loadBoard(sprintId);
      },
      error: (err) => this.error.set(err?.message || 'Failed to cancel sprint.')
    });
  }

  saveSprintCapacity(): void {
    const sprint = this.selectedSprint();
    const sprintId = this.selectedSprintId();
    if (!sprint || !sprintId) return;
    this.scrumApi.updateSprint(sprintId, {
      name: sprint.name,
      startDate: sprint.startDate,
      endDate: sprint.endDate,
      goal: sprint.goal,
      capacityPoints: this.editCapacityPoints ?? undefined,
      capacityHours: this.editCapacityHours ?? undefined
    }).subscribe({
      next: () => this.loadBoard(sprintId),
      error: (err) => this.error.set(err?.message || 'Failed to update sprint capacity.')
    });
  }

  committedPoints(sprint: SprintDto | null): number {
    if (!sprint) return 0;
    return sprint.committedPointsAtStart
      ?? sprint.committedStoryPoints
      ?? sprint.committedPoints
      ?? sprint.totalStoryPoints
      ?? 0;
  }

  committedHours(sprint: SprintDto | null): number {
    if (!sprint) return 0;
    return sprint.committedHours ?? 0;
  }

  loadRetrospective(sprintId: string): void {
    this.scrumApi.getRetrospective(sprintId).subscribe({
      next: (retro) => {
        this.retrospective.set(retro);
        this.retroWhatWentWell = retro.whatWentWell ?? '';
        this.retroWhatToImprove = retro.whatToImprove ?? '';
        this.retroActionItems = retro.actionItems ?? '';
      },
      error: () => this.retrospective.set(null)
    });
  }

  saveRetrospective(): void {
    const sprintId = this.selectedSprintId();
    if (!sprintId) return;
    this.savingRetro.set(true);
    this.scrumApi.upsertRetrospective(sprintId, {
      whatWentWell: this.retroWhatWentWell,
      whatToImprove: this.retroWhatToImprove,
      actionItems: this.retroActionItems
    }).subscribe({
      next: (retro) => {
        this.retrospective.set(retro);
        this.savingRetro.set(false);
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to save retrospective.');
        this.savingRetro.set(false);
      }
    });
  }

  drop(event: CdkDragDrop<BacklogIssueDto[]>, column: BoardColumnView): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      return;
    }

    transferArrayItem(
      event.previousContainer.data,
      event.container.data,
      event.previousIndex,
      event.currentIndex
    );

    const issue = event.container.data[event.currentIndex];
    const sprintId = this.selectedSprintId();
    const status = column.statuses?.[0];
    if (!issue || !sprintId || status === undefined) return;

    this.scrumApi.moveOnBoard(sprintId, issue.id, status).subscribe({
      next: (updated) => {
        issue.status = updated.status;
        issue.statusLabel = updated.statusLabel;
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to update status.');
        this.loadBoard(sprintId);
      }
    });
  }

  connectedLists(): string[] {
    return this.columns().map((c) => c.key);
  }

  canStart(): boolean {
    return this.selectedSprint()?.status === 0;
  }

  canComplete(): boolean {
    return this.selectedSprint()?.status === 1;
  }

  canMoveBoard(): boolean {
    const status = this.selectedSprint()?.status;
    return status === 0 || status === 1;
  }

  openIssueDrawer(issue: BacklogIssueDto, event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    this.issueDrawer.openById(issue.id);
  }

  priorityBadgeClass(priority: number): string {
    const map: Record<number, string> = {
      0: 'tt-badge--priority-lowest',
      1: 'tt-badge--priority-low',
      2: 'tt-badge--priority-medium',
      3: 'tt-badge--priority-high',
      4: 'tt-badge--priority-highest'
    };
    return map[priority] ?? 'tt-badge--priority-medium';
  }

  deadlineBadgeClass = deadlineBadgeClass;
  issueDeadlineLabel = deadlineDisplayLabel;
}
